import { COMPANY } from '../../lib/company';
import { detectLegalLang, type LegalLang } from '../../lib/legalLang';
import { CompanyDetails, PrevailsNote, UpdatedLine } from './LegalChrome';

/** The Privacy Notice. Portuguese is the authoritative text; English is a courtesy translation. */
export function PrivacyContent({ lang = detectLegalLang() }: { lang?: LegalLang }) {
  return lang === 'pt' ? <PrivacyPt /> : <PrivacyEn />;
}

function PrivacyEn() {
  return (
    <div className="prose-legal">
      <UpdatedLine lang="en" />

      <h2>The short version</h2>
      <ul>
        <li>We collect what we need to match you with a local host, run your trip and take payments: your account, your taste (including your chats with Alma), your trips and payments.</li>
        <li>We don't sell your personal data.</li>
        <li>Your chats with Alma are processed by an AI provider and may be used to improve our AI. Tell Alma your taste, not your identity.</li>
        <li>You can ask to see, correct, export or delete your data, and you can delete your account in Settings.</li>
      </ul>

      <h2>1. Who is responsible</h2>
      <p>The controller of your personal data is:</p>
      <CompanyDetails lang="en" />
      <p>For anything about your data, write to <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.</p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Account:</strong> name, email address, profile photo, how you sign in (email and password, Google or Apple), role (traveller or host), and the settings you choose.</li>
        <li><strong>Your taste and Alma chats:</strong> what you write to Alma, the taste profile we build from it, and your trip preferences (cities, dates, number of days).</li>
        <li><strong>Trips and messages:</strong> your matches, bookings, the messages you exchange with your host, and your feedback after a trip.</li>
        <li><strong>Hosts:</strong> host profile and the details needed to pay and invoice you (legal name, tax number, bank account).</li>
        <li><strong>Payments:</strong> amounts, status and refunds. Card details are entered with Stripe and never reach us.</li>
        <li><strong>Device and use:</strong> push-notification token, app version, and how the app is used (analytics and error reports).</li>
        <li><strong>Location:</strong> only if you choose to share it, for example to fill in an address.</li>
      </ul>

      <h2>3. Why we use it, and on what basis</h2>
      <ul>
        <li><strong>To provide the Service</strong> (create your account, match you with a host, run your trip, take and pay out payments): needed to perform our contract with you.</li>
        <li><strong>To improve Sekretly and our AI, keep it secure and prevent fraud:</strong> our legitimate interests, which we balance against your rights.</li>
        <li><strong>To meet legal obligations</strong>, such as tax and accounting records.</li>
        <li><strong>Push notifications and location:</strong> your consent, which you can withdraw in your phone's settings.</li>
      </ul>

      <h2>4. Alma and AI</h2>
      <p>Alma's replies are produced by an AI provider that processes your messages for us. We use your conversations to build your taste profile and to improve our AI. Please don't put names, addresses, phone numbers, ID numbers, financial details or health information in the chat. Chat content kept for AI training is de-identified and dissociated from your identity when you delete your account.</p>

      <h2>5. Who we share it with</h2>
      <ul>
        <li><strong>Your host</strong> gets what they need to prepare your trip: your first name, your taste profile and your trip details. Hosts must keep it confidential.</li>
        <li><strong>Service providers</strong> that process data for us: Supabase (database and sign-in), Cloudflare (hosting), Stripe (payments), Resend (email), Anthropic (Alma), PostHog (analytics and error reports), Google Firebase and Apple (push notifications), OpenStreetMap (address search).</li>
        <li><strong>Authorities</strong>, when the law requires it.</li>
      </ul>
      <p>We do not sell your personal data.</p>

      <h2>6. Transfers outside the EU/EEA</h2>
      <p>Some of these providers handle data outside the EU/EEA. When they do, we rely on the safeguards recognised by EU law, such as standard contractual clauses.</p>

      <h2>7. How long we keep it</h2>
      <p>We keep your data while your account exists. When you delete your account we erase your personal data, except what the law requires us to keep (such as transaction and invoice records) for the legally required period. Chat content kept for AI training is de-identified and dissociated from you.</p>

      <h2>8. Your rights</h2>
      <p>You can ask us to give you access to your data, correct it, delete it, restrict or object to its use, or send it to you in a portable format. Where we rely on your consent, you can withdraw it at any time. Write to <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>; we answer within one month. You can delete your account in Settings. You also have the right to complain to the Portuguese data protection authority, the Comissão Nacional de Proteção de Dados (CNPD, www.cnpd.pt).</p>

      <h2>9. Security</h2>
      <p>We use technical and organisational measures to protect your data, including encryption, access controls and monitoring. No system is perfectly secure; please use a strong password and tell us if you notice anything suspicious.</p>

      <h2>10. Browser storage</h2>
      <p>The website and app store small pieces of data on your device (local storage) to keep you signed in and remember your choices, and use analytics to understand how Sekretly is used.</p>

      <h2>11. Children</h2>
      <p>Sekretly is for adults. You must be at least 18 to use it.</p>

      <h2>12. Changes</h2>
      <p>We may update this notice. For material changes we will give reasonable notice.</p>

      <PrevailsNote lang="en" />
    </div>
  );
}

function PrivacyPt() {
  return (
    <div className="prose-legal">
      <UpdatedLine lang="pt" />

      <h2>A versão curta</h2>
      <ul>
        <li>Recolhemos o que precisamos para o associar a um anfitrião local, organizar a sua viagem e receber pagamentos: a sua conta, o seu gosto (incluindo as conversas com a Alma), as suas viagens e pagamentos.</li>
        <li>Não vendemos os seus dados pessoais.</li>
        <li>As suas conversas com a Alma são tratadas por um fornecedor de IA e podem ser usadas para melhorar a nossa IA. Diga à Alma o seu gosto, não a sua identidade.</li>
        <li>Pode pedir para ver, corrigir, exportar ou apagar os seus dados e pode eliminar a sua conta nas Definições.</li>
      </ul>

      <h2>1. Quem é o responsável</h2>
      <p>O responsável pelo tratamento dos seus dados pessoais é:</p>
      <CompanyDetails lang="pt" />
      <p>Para qualquer assunto sobre os seus dados, escreva para <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.</p>

      <h2>2. O que recolhemos</h2>
      <ul>
        <li><strong>Conta:</strong> nome, endereço de email, fotografia de perfil, forma de início de sessão (email e palavra-passe, Google ou Apple), perfil (viajante ou anfitrião) e as definições que escolher.</li>
        <li><strong>O seu gosto e as conversas com a Alma:</strong> o que escreve à Alma, o perfil de gosto que construímos a partir disso e as preferências de viagem (cidades, datas, número de dias).</li>
        <li><strong>Viagens e mensagens:</strong> as suas associações, reservas, as mensagens trocadas com o anfitrião e o seu feedback depois da viagem.</li>
        <li><strong>Anfitriões:</strong> o perfil de anfitrião e os dados necessários para lhe pagar e emitir faturas (nome legal, número fiscal, conta bancária).</li>
        <li><strong>Pagamentos:</strong> montantes, estado e reembolsos. Os dados do cartão são introduzidos na Stripe e nunca chegam até nós.</li>
        <li><strong>Dispositivo e utilização:</strong> identificador para notificações push, versão da aplicação e a forma como a aplicação é usada (análise de utilização e relatórios de erros).</li>
        <li><strong>Localização:</strong> apenas se escolher partilhá-la, por exemplo para preencher uma morada.</li>
      </ul>

      <h2>3. Para que usamos e com que fundamento</h2>
      <ul>
        <li><strong>Prestar o Serviço</strong> (criar a sua conta, associá-lo a um anfitrião, organizar a viagem, receber e pagar pagamentos): necessário para a execução do contrato consigo.</li>
        <li><strong>Melhorar a Sekretly e a nossa IA, manter a segurança e prevenir fraude:</strong> os nossos interesses legítimos, que ponderamos face aos seus direitos.</li>
        <li><strong>Cumprir obrigações legais</strong>, como os registos fiscais e contabilísticos.</li>
        <li><strong>Notificações push e localização:</strong> o seu consentimento, que pode retirar nas definições do telemóvel.</li>
      </ul>

      <h2>4. A Alma e a IA</h2>
      <p>As respostas da Alma são produzidas por um fornecedor de IA que trata as suas mensagens por nossa conta. Usamos as suas conversas para construir o seu perfil de gosto e para melhorar a nossa IA. Por favor não escreva na conversa nomes, moradas, números de telefone, números de documentos, dados financeiros ou informação de saúde. O conteúdo das conversas conservado para treino de IA é anonimizado e dissociado da sua identidade quando elimina a sua conta.</p>

      <h2>5. Com quem partilhamos</h2>
      <ul>
        <li><strong>O seu anfitrião</strong> recebe o que precisa para preparar a sua viagem: o seu primeiro nome, o seu perfil de gosto e os dados da viagem. Os anfitriões têm de os manter confidenciais.</li>
        <li><strong>Prestadores de serviços</strong> que tratam dados por nossa conta: Supabase (base de dados e início de sessão), Cloudflare (alojamento), Stripe (pagamentos), Resend (email), Anthropic (Alma), PostHog (análise e relatórios de erros), Google Firebase e Apple (notificações push), OpenStreetMap (pesquisa de moradas).</li>
        <li><strong>Autoridades</strong>, quando a lei o exigir.</li>
      </ul>
      <p>Não vendemos os seus dados pessoais.</p>

      <h2>6. Transferências para fora da UE/EEE</h2>
      <p>Alguns destes prestadores tratam dados fora da UE/EEE. Quando isso acontece, recorremos às garantias reconhecidas pelo direito da União Europeia, como as cláusulas contratuais-tipo.</p>

      <h2>7. Durante quanto tempo conservamos</h2>
      <p>Conservamos os seus dados enquanto a sua conta existir. Quando elimina a conta, apagamos os seus dados pessoais, exceto o que a lei nos obriga a conservar (como os registos de transações e faturas) durante o período legalmente exigido. O conteúdo das conversas conservado para treino de IA é anonimizado e dissociado de si.</p>

      <h2>8. Os seus direitos</h2>
      <p>Pode pedir-nos acesso aos seus dados, a sua retificação ou apagamento, a limitação ou oposição ao seu tratamento, ou que lhos enviemos num formato portátil. Quando nos baseamos no seu consentimento, pode retirá-lo a qualquer momento. Escreva para <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>; respondemos no prazo de um mês. Pode eliminar a sua conta nas Definições. Tem também o direito de apresentar reclamação à autoridade portuguesa de proteção de dados, a Comissão Nacional de Proteção de Dados (CNPD, www.cnpd.pt).</p>

      <h2>9. Segurança</h2>
      <p>Usamos medidas técnicas e organizativas para proteger os seus dados, incluindo cifragem, controlo de acessos e monitorização. Nenhum sistema é perfeitamente seguro; use uma palavra-passe forte e avise-nos se notar algo suspeito.</p>

      <h2>10. Armazenamento no navegador</h2>
      <p>O website e a aplicação guardam pequenas quantidades de dados no seu dispositivo (armazenamento local) para o manter com sessão iniciada e recordar as suas escolhas, e usam ferramentas de análise para perceber como a Sekretly é utilizada.</p>

      <h2>11. Menores</h2>
      <p>A Sekretly destina-se a adultos. Tem de ter pelo menos 18 anos para a usar.</p>

      <h2>12. Alterações</h2>
      <p>Podemos atualizar este aviso. Em caso de alterações relevantes daremos um aviso razoável.</p>

      <PrevailsNote lang="pt" />
    </div>
  );
}
